/* ==========================================================================
   PREDICTIVE SEARCH
   Uses Shopify's /search/suggest endpoint with the theme's own result
   partial, so the suggestion markup lives in Liquid rather than in a string
   template here.
   ========================================================================== */

(function () {
  'use strict';

  const Theme = window.Theme;
  const routes = window.routes || {};

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-search-results]');
      this.idle = this.querySelector('[data-search-idle]');
      this.loading = this.querySelector('[data-search-loading]');
      this.clearButton = this.querySelector('[data-search-clear]');
      this.form = this.querySelector('form');

      if (!this.input) return;

      this.controller = null;
      this.cache = new Map();
      this.minLength = Number(this.dataset.minLength || 2);

      this.onInput = Theme.debounce(() => this.search(), 220);
      this.input.addEventListener('input', () => {
        this.toggleClear();
        this.onInput();
      });

      this.input.addEventListener('keydown', (event) => this.onKeydown(event));

      if (this.clearButton) {
        this.clearButton.addEventListener('click', () => {
          this.input.value = '';
          this.input.focus();
          this.toggleClear();
          this.reset();
        });
      }

      // Re-focus the field whenever the overlay that holds it opens.
      const overlay = this.closest('theme-overlay');
      if (overlay) {
        overlay.addEventListener('transitionend', () => {
          if (overlay.classList.contains('is-open')) this.input.focus();
        });
      }

      this.toggleClear();
    }

    toggleClear() {
      if (this.clearButton) {
        this.clearButton.classList.toggle('is-visible', this.input.value.length > 0);
      }
    }

    reset() {
      if (this.controller) this.controller.abort();
      if (this.results) {
        this.results.innerHTML = '';
        this.results.hidden = true;
      }
      if (this.idle) this.idle.hidden = false;
      if (this.loading) this.loading.hidden = true;
      this.setExpanded(false);
    }

    setExpanded(expanded) {
      this.input.setAttribute('aria-expanded', String(expanded));
    }

    search() {
      const query = this.input.value.trim();

      if (query.length < this.minLength) {
        this.reset();
        return;
      }

      if (this.cache.has(query)) {
        this.render(this.cache.get(query));
        return;
      }

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      if (this.idle) this.idle.hidden = true;
      if (this.loading) this.loading.hidden = false;

      const params = new URLSearchParams({
        q: query,
        'resources[type]': this.dataset.resources || 'product,collection,page,query',
        'resources[limit]': this.dataset.limit || '6',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,product_type,variants.title,vendor,tag',
        section_id: this.dataset.sectionId || 'predictive-search',
      });

      fetch((routes.predictiveSearch || '/search/suggest') + '?' + params.toString(), {
        signal: this.controller.signal,
      })
        .then((response) => {
          if (!response.ok) throw new Error('Search unavailable');
          return response.text();
        })
        .then((html) => {
          const section = Theme.parseSection(html, '[data-predictive-results]');
          const markup = section ? section.innerHTML : '';
          this.cache.set(query, markup);
          this.render(markup);
        })
        .catch((error) => {
          if (error.name === 'AbortError') return;
          if (this.loading) this.loading.hidden = true;
          if (this.results) {
            this.results.hidden = false;
            this.results.innerHTML =
              '<p class="predictive__empty-text">' +
              (Theme.strings.searchError || 'Search is unavailable right now.') +
              '</p>';
          }
        });
    }

    render(markup) {
      if (this.loading) this.loading.hidden = true;
      if (this.idle) this.idle.hidden = true;
      if (!this.results) return;

      this.results.innerHTML = markup;
      this.results.hidden = false;
      this.setExpanded(true);
      this.activeIndex = -1;
    }

    get options() {
      return this.results ? Array.from(this.results.querySelectorAll('[data-search-option]')) : [];
    }

    onKeydown(event) {
      const options = this.options;

      if (event.key === 'Enter') {
        const active = options[this.activeIndex];
        if (active) {
          event.preventDefault();
          active.click();
        }
        return;
      }

      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      if (options.length === 0) return;

      event.preventDefault();
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      this.activeIndex = (this.activeIndex + delta + options.length) % options.length;

      options.forEach((option, index) => {
        const active = index === this.activeIndex;
        option.setAttribute('aria-selected', String(active));
        if (active) option.scrollIntoView({ block: 'nearest' });
      });

      this.input.setAttribute('aria-activedescendant', options[this.activeIndex].id || '');
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
})();
